import React, { useState } from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import { Box, FormControlLabel, MenuItem, Radio, Select } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';

export const stepTitle = 'Just two quick details left';
export const stepDescription =
  'Who pays for the application fee and select your rental application.';

const ApplicationFeeSettings: React.FC = () => {
  const [paymentOption, setPaymentOption] = useState('applicant');
  const [selectedApplication, setSelectedApplication] =
    useState('rentspree-default');

  const handleApplicationChange = (event: any) => {
    setSelectedApplication(event.target.value);
  };

  const getApplicationFee = () => {
    if (paymentOption === 'applicant') {
      return '$0.00';
    } else {
      return '$49.00'; // Example fee when landlord pays
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 600,
        margin: '0 auto',
        padding: { xs: 2, md: 4 },
      }}
    >
      {/* Application Fee Payment Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AttachMoneyIcon sx={{ color: '#1976d2', fontSize: 24 }} />
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Who pays for the application fee?
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0 }}>
          <FormControlLabel
            value='applicant'
            control={<Radio sx={{ display: 'none' }} />}
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor:
                    paymentOption === 'applicant' ? '#222222' : '#f5f5f5',
                  color: paymentOption === 'applicant' ? 'white' : '#333',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  border: '1px solid #e0e0e0',
                  borderRight: 'none',
                  '&:hover': {
                    backgroundColor:
                      paymentOption === 'applicant' ? '#222222' : '#e5e5e5',
                  },
                }}
              >
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  Applicant Pays
                </Typography>
              </Box>
            }
            sx={{ margin: 0, flex: 1 }}
            onClick={() => setPaymentOption('applicant')}
          />

          <FormControlLabel
            value='landlord'
            control={<Radio sx={{ display: 'none' }} />}
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  padding: '12px 24px',
                  backgroundColor:
                    paymentOption === 'landlord' ? '#222222' : '#f5f5f5',
                  color: paymentOption === 'landlord' ? 'white' : '#333',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderTopRightRadius: '8px',
                  borderBottomRightRadius: '8px',
                  border: '1px solid #e0e0e0',
                  borderLeft: 'none',
                  '&:hover': {
                    backgroundColor:
                      paymentOption === 'landlord' ? '#222222' : '#e5e5e5',
                  },
                }}
              >
                <Typography variant='body1' sx={{ fontWeight: 500 }}>
                  I'll Pay
                </Typography>
              </Box>
            }
            sx={{ margin: 0, flex: 1 }}
            onClick={() => setPaymentOption('landlord')}
          />
        </Box>

        <Typography
          variant='body2'
          sx={{
            mt: 2,
            color: '#666',
            fontWeight: 400,
          }}
        >
          Your cost:{' '}
          <Box component='span' sx={{ color: '#333', fontWeight: 600 }}>
            {getApplicationFee()}
          </Box>
        </Typography>
      </Box>

      {/* Rental Application Section */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DescriptionIcon sx={{ color: '#1976d2', fontSize: 24 }} />
          <Typography
            variant='h6'
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Select rental application
          </Typography>
        </Box>

        <Select
          value={selectedApplication}
          onChange={handleApplicationChange}
          fullWidth
          displayEmpty
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#94a3b8',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            },
          }}
        >
          <MenuItem value='rentspree-default'>
            RentSpree Application (Default)
          </MenuItem>
          <MenuItem value='custom-application'>Custom Application</MenuItem>
          <MenuItem value='third-party'>Third Party Application</MenuItem>
        </Select>
      </Box>
    </Box>
  );
};

export default ApplicationFeeSettings;
